import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuthStore } from '../store/authStore';
import { login, register } from '../api/auth';

function apiErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
    if (err.response?.status === 429) return 'Too many attempts. Please wait a minute and try again.';
  }
  return fallback;
}

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login: storeLogin } = useAuthStore();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (mode === 'register' && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);

    try {
      if (mode === 'register') {
        const data = await register({ username, email, password });
        storeLogin(data.token, data.user);
        navigate('/dashboard');
      } else {
        const data = await login({ username, password });
        storeLogin(data.token, data.user);
        navigate('/dashboard');
      }
    } catch (err) {
      setError(apiErrorMessage(err, `${mode === 'register' ? 'Registration' : 'Login'} failed. Please try again.`));
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setMode((m) => (m === 'login' ? 'register' : 'login'));
    setError('');
    setConfirmPassword('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative overflow-hidden"
      style={{ background: 'var(--bg)' }}
    >
      {/* Bokeh orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute rounded-full"
          style={{
            width: 500, height: 500,
            background: 'radial-gradient(circle, rgba(80,60,220,0.55) 0%, transparent 70%)',
            top: -150, left: -100,
            filter: 'blur(60px)', opacity: 0.35,
          }}
        />
        <div className="absolute rounded-full"
          style={{
            width: 400, height: 400,
            background: 'radial-gradient(circle, rgba(108,99,255,0.22) 0%, transparent 70%)',
            bottom: -100, right: -80,
            filter: 'blur(60px)', opacity: 0.35,
          }}
        />
        <div className="absolute rounded-full"
          style={{
            width: 300, height: 300,
            background: 'radial-gradient(circle, rgba(60,40,200,0.3) 0%, transparent 70%)',
            top: '40%', left: '60%',
            filter: 'blur(60px)', opacity: 0.35,
          }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-sm glass-strong rounded-3xl p-10 md:p-11"
        style={{
          boxShadow: '0 0 0 1px rgba(255,255,255,0.04) inset, 0 40px 80px rgba(0,0,0,0.6), 0 0 60px rgba(60,80,200,0.06)',
        }}
      >
        <div className="text-center mb-8">
          <h1 className="font-display text-[26px] font-bold text-gold-text tracking-tight mb-1">
            GRE <span className="text-cream italic font-normal">Vocab Studio</span>
          </h1>
          <p className="text-[12px] font-medium uppercase tracking-[0.06em] text-text-dim">
            Intelligent vocabulary learning
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="relative">
            <label className="block text-[11px] font-medium uppercase tracking-[0.12em] text-text-dim mb-2 transition-colors duration-150"
              style={{ color: username ? 'var(--gold)' : undefined }}
            >
              Username
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              className="w-full bg-white/[0.04] border-0 border-b-[1.5px] border-white/10 px-0.5 py-2.5 text-cream text-sm font-normal outline-none transition-colors duration-200 focus:border-gold placeholder:text-text-dim"
              placeholder="Enter username"
            />
          </div>

          <AnimatePresence>
            {mode === 'register' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="relative overflow-hidden"
              >
                <label className="block text-[11px] font-medium uppercase tracking-[0.12em] text-text-dim mb-2 transition-colors duration-150"
                  style={{ color: email ? 'var(--gold)' : undefined }}
                >
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required={mode === 'register'}
                  className="w-full bg-white/[0.04] border-0 border-b-[1.5px] border-white/10 px-0.5 py-2.5 text-cream text-sm font-normal outline-none transition-colors duration-200 focus:border-gold placeholder:text-text-dim"
                  placeholder="Enter email"
                />
              </motion.div>
            )}
          </AnimatePresence>

          <div className="relative">
            <label className="block text-[11px] font-medium uppercase tracking-[0.12em] text-text-dim mb-2 transition-colors duration-150"
              style={{ color: password ? 'var(--gold)' : undefined }}
            >
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={mode === 'register' ? 8 : undefined}
              className="w-full bg-white/[0.04] border-0 border-b-[1.5px] border-white/10 px-0.5 py-2.5 text-cream text-sm font-normal outline-none transition-colors duration-200 focus:border-gold placeholder:text-text-dim"
              placeholder={mode === 'register' ? 'At least 8 characters' : 'Enter password'}
            />
          </div>

          <AnimatePresence>
            {mode === 'register' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="relative overflow-hidden"
              >
                <label className="block text-[11px] font-medium uppercase tracking-[0.12em] text-text-dim mb-2 transition-colors duration-150"
                  style={{ color: confirmPassword ? 'var(--gold)' : undefined }}
                >
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required={mode === 'register'}
                  minLength={8}
                  className="w-full bg-white/[0.04] border-0 border-b-[1.5px] border-white/10 px-0.5 py-2.5 text-cream text-sm font-normal outline-none transition-colors duration-200 focus:border-gold placeholder:text-text-dim"
                  placeholder="Repeat password"
                />
              </motion.div>
            )}
          </AnimatePresence>

          {mode === 'login' && (
            <p className="text-right -mt-2">
              <Link
                to="/forgot-password"
                className="text-[11px] text-text-dim hover:text-gold-text transition-colors"
              >
                Forgot password?
              </Link>
            </p>
          )}

          {error && (
            <motion.p
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-red text-xs text-center font-medium min-h-4"
            >
              {error}
            </motion.p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 mt-3 btn-gold rounded-xl text-[13px] font-semibold tracking-wide relative overflow-hidden disabled:opacity-50 disabled:cursor-default disabled:transform-none"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                {mode === 'register' ? 'Creating account...' : 'Signing in...'}
              </span>
            ) : mode === 'register' ? (
              'Create Account'
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        <p className="text-center text-xs text-text-dim mt-6">
          {mode === 'login' ? (
            <>
              Don't have an account?{' '}
              <button
                type="button"
                onClick={toggleMode}
                className="text-gold-text hover:text-gold-light font-medium transition-colors"
              >
                Get Started
              </button>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <button
                type="button"
                onClick={toggleMode}
                className="text-gold-text hover:text-gold-light font-medium transition-colors"
              >
                Sign In
              </button>
            </>
          )}
        </p>
      </motion.div>
    </div>
  );
};
